import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { DirectProperty, PropertiesService } from '@app/shared/services/properties/properties.service';
import { Observable, of } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';
import { DirectChannel, DirectService } from '../../services/direct.service';

@Component({
  standalone: false,
  templateUrl: './direct-channels-list.component.html',
  styleUrls: ['./direct-channels-list.component.scss'],
})
export class DirectChannelsListComponent {
  saving: boolean = false;
  saveStatus: string = '';
  isMobile$ = this.breakpointObserver.observe(['(max-width: 1024px)']).pipe(map((res) => res.matches));
  gvrStats$ = this.directService.fetchChannelMetrics(DirectChannel.GVR);
  @ViewChild('snackbarTemplate') snackbarTemplate: TemplateRef<any>;

  properties$: Observable<DirectProperty[]> = this.propertiesService.getDirectProperties().pipe(
    map((properties) =>
      properties.sort((a, b) => {
        // Check if property has google_vacation_rentals_sync
        const aSync = a.google_vacation_rentals_sync;
        const bSync = b.google_vacation_rentals_sync;

        // If only one of the properties is synced, prioritize that one
        if (aSync && !bSync) return -1;
        if (!aSync && bSync) return 1;

        // If both properties are synced or neither are, further criteria are needed
        if (aSync && bSync) {
          // Check if property has gvr status as 'live'
          const aLive = a.gvr?.status === 'live';
          const bLive = b.gvr?.status === 'live';

          // If only one of the properties has status 'live', prioritize that one
          if (aLive && !bLive) return -1;
          if (!aLive && bLive) return 1;
        }

        // Sort by name
        return a.name.localeCompare(b.name);
      })
    )
  );

  barRef: any;

  constructor(
    private directService: DirectService,
    private propertiesService: PropertiesService,
    private snackbar: MatSnackBar,
    private breakpointObserver: BreakpointObserver
  ) { }

  enableProperty(property: DirectProperty, event: MatCheckboxChange) {
    property.google_vacation_rentals_sync = event.checked;

    if (event.checked && !property.google_vacation_rentals_site_id && property.eligible_sites.length > 0) {
      property.google_vacation_rentals_site_id = property.eligible_sites[0].site_id;
    }
  }

  setAllPropertiesAsUnselected() {
    this.properties$.pipe(take(1)).subscribe((properties) => {
      // Update properties
      const updatedProperties = properties.map((property) => {
        property.google_vacation_rentals_sync = false;
        return property;
      });

      // Update properties$ variable
      this.properties$ = of(updatedProperties);
    });
  }

  // Mark all properties that do not have a block for the GVR check as checked.
  selectAllEligibleProperties() {
    // Get the current properties
    this.properties$.pipe(take(1)).subscribe((properties) => {
      // Update properties
      const updatedProperties = properties.map((property) => {
        const isBlocked = property.checks.some(
          (check) => check.id === 'google_vacation_rentals' && check.result === 'block'
        );

        if (!isBlocked) {
          property.google_vacation_rentals_sync = true;
        }

        if (!property.google_vacation_rentals_site_id && property.eligible_sites.length > 0) {
          property.google_vacation_rentals_site_id = property.eligible_sites[0].site_id;
        }

        return property;
      });

      // Update properties$ variable
      this.properties$ = of(updatedProperties);
    });
  }

  // Save changes across multiple properties
  saveChanges(properties: DirectProperty[]) {
    this.saveStatus = 'Saving...';

    // Get all the properties with google_vacation_rentals_sync set to true
    const selectedProperties = properties.filter((property) => property.google_vacation_rentals_sync);

    const payload = selectedProperties.map((property) => {
      return {
        property_id: property.id,
        site_id: property.google_vacation_rentals_site_id,
      };
    });

    this.propertiesService
      .updateGoogleVacationRentalsForAllProperties(payload)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.saveStatus = '';
          }, 2000);
        })
      )
      .subscribe(
        () => {
          this.saveStatus = 'Saved successfully';
        },
        () => {
          this.saveStatus = 'Save error';
        }
      );
  }

  allEligiblePropertiesSelected(properties: DirectProperty[]) {
    const eligibleProperties = properties.filter(
      (property) => !property.checks.some((check) => check.id === 'google_vacation_rentals' && check.result === 'block')
    );
    return eligibleProperties.every((property) => property.google_vacation_rentals_sync);
  }

  // When the user manually picks a new site from the dropdown, we want to offer them the ability to update all properties that are eligible and also belong to the selected site
  offerSuggestionToUpdateAllProperties(site, properties) {
    this.barRef = this.snackbar.openFromTemplate(this.snackbarTemplate, {
      data: {
        site,
        properties,
      },
      panelClass: 'neutral-snackbar',
    });
  }

  updateOtherEligiblePropertiesToSite(site, properties) {
    // Find a list of all properties that are eligible
    const eligibleProperties = properties.filter(
      (property) => !property.checks.some((check) => check.id === 'google_vacation_rentals' && check.result === 'block')
    );

    if (eligibleProperties.length === 0) {
      return;
    }

    // Find a list of eligibleProperties which have an `eligible_site` matching the site.url
    const eligiblePropertiesWithMatchingSite = eligibleProperties.filter((property) =>
      property.eligible_sites.some((eligibleSite) => eligibleSite.url === site.url)
    );

    // foreach eligiblePropertiesWithMatchingSite, update their google_vacation_rentals_site_id to the site.site_id
    const updatedProperties = eligiblePropertiesWithMatchingSite.map((property) => {
      property.google_vacation_rentals_site_id = site.site_id;
      return property;
    });

    // Remove updated properties from original properties array
    const filteredProperties = properties.filter(
      (property) => !updatedProperties.some((updatedProperty) => updatedProperty.id === property.id)
    );

    const mergedProperties = [...updatedProperties, ...filteredProperties];

    // Update properties$ variable
    this.properties$ = of(mergedProperties);

    this.barRef.dismiss();
  }

  removeSchemaFromUrl(url: string): string {
    return url.replace(/(^\w+:|^)\/\//, '');
  }
}
